import React, { useEffect, useState } from 'react';
import { factorGuides } from '../data/factorGuides';
import { factorTitle, qualitativeLabel } from '../lib/strength';

export default function FactorDrawer({ factor, factorIndex, totalFactors, initialValue, onApply, onClose }) {
  const guide = factorGuides[factor];
  const [picked, setPicked] = useState(initialValue);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!guide) return null;

  return (
    <>
      <div style={overlayStyle} onClick={onClose} aria-hidden="true" />
      <aside style={drawerStyle} role="dialog" aria-label={`Edit ${factorTitle(factor)}`}>
        <header style={headStyle}>
          <div style={{ flex: 1 }}>
            <div style={eyebrowStyle}>Step 1 · Factor {factorIndex} of {totalFactors}</div>
            <h2 style={drawerNameStyle}>{factorTitle(factor)}</h2>
            <p style={subStyle}>{guide.what}</p>
          </div>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div style={bodyStyle}>
          <Section title="Rating scale (1–5)">
            {[1, 2, 3, 4, 5].map((n) => {
              const isPicked = n === picked;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPicked(n)}
                  style={isPicked ? scaleRowActiveStyle : scaleRowStyle}
                  data-rating={n}
                  aria-pressed={isPicked}
                >
                  <div style={isPicked ? scaleNumActiveStyle : scaleNumStyle}>{n}</div>
                  <div style={{ flex: 1 }}>
                    <div style={scaleLabelStyle}>
                      {qualitativeLabel(factor, n)}
                      {isPicked && <span style={pickedTag}>· your rating</span>}
                    </div>
                    <div style={scaleDescStyle}>{guide.scale[n - 1]}</div>
                  </div>
                </button>
              );
            })}
          </Section>

          <Section title="How buyers see this">
            <p style={proseStyle}>{guide.buyerCare}</p>
          </Section>

          <Section title="How to assess">
            <p style={proseStyle}>{guide.howToAssess}</p>
          </Section>

          <Section title="Benchmarks">
            <p style={proseStyle}>{guide.benchmarks}</p>
          </Section>
        </div>

        <footer style={footStyle}>
          <button type="button" onClick={onClose} style={ghostBtnStyle}>Cancel</button>
          <button
            type="button"
            onClick={() => onApply(picked)}
            style={picked ? accentBtnStyle : accentBtnDisabledStyle}
            disabled={!picked}
          >
            {picked ? `Apply rating · ${picked}` : 'Pick a rating'}
          </button>
        </footer>
      </aside>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section style={sectionStyle}>
      <div style={sectionTitleStyle}>{title}</div>
      {children}
    </section>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(31, 26, 18, 0.45)',
  zIndex: 50,
  animation: 'loopora-fade-in 0.25s ease-out',
};
const drawerStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: '480px',
  maxWidth: '90vw',
  background: 'var(--paper)',
  borderLeft: '1px solid var(--line)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 60,
  boxShadow: 'var(--shadow)',
  fontFamily: 'var(--font)',
  // Slide in from the right edge on mount (Sarah feedback #10 — the
  // factor drawer panel, not the running-estimate rail).
  animation: 'loopora-slide-in-right 0.32s cubic-bezier(0.22, 1, 0.36, 1)',
};

const headStyle = {
  display: 'flex',
  gap: '12px',
  padding: '20px 24px 16px',
  borderBottom: '1px solid var(--line)',
};
const eyebrowStyle = { fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' };
const drawerNameStyle = { fontFamily: 'var(--display)', fontWeight: 500, fontSize: '28px', color: 'var(--ink)', letterSpacing: '-0.02em', margin: '6px 0 8px' };
const subStyle = { fontSize: '13px', color: 'var(--ink2)', margin: 0, lineHeight: 1.55 };

const closeBtnStyle = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  border: '1px solid var(--line)',
  background: 'transparent',
  color: 'var(--ink2)',
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
};

const bodyStyle = { padding: '20px 24px', overflowY: 'auto', flex: 1 };
const sectionStyle = { marginBottom: '24px' };
const sectionTitleStyle = {
  fontSize: '11px',
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: '10px',
};
const proseStyle = { fontSize: '13px', color: 'var(--ink2)', lineHeight: 1.55, margin: 0 };

// React strips shorthand+longhand border-* conflicts; use longhands so
// the spread+borderColor override in scaleRowActiveStyle merges cleanly.
const scaleRowBase = {
  display: 'flex',
  gap: '12px',
  padding: '12px 14px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--line)',
  borderRadius: '6px',
  background: 'var(--surface)',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
  width: '100%',
  marginBottom: '6px',
};
const scaleRowStyle = { ...scaleRowBase };
const scaleRowActiveStyle = { ...scaleRowBase, borderColor: 'var(--accent)', background: 'var(--accent-bg)' };

const scaleNumBase = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  fontFamily: 'var(--display)',
  fontStyle: 'italic',
  fontSize: '18px',
  fontWeight: 500,
  flexShrink: 0,
};
const scaleNumStyle = { ...scaleNumBase, color: 'var(--muted)', background: 'var(--line-dim)' };
const scaleNumActiveStyle = { ...scaleNumBase, color: '#fff', background: 'var(--accent)' };

const scaleLabelStyle = { fontSize: '13.5px', fontWeight: 500, color: 'var(--ink)', marginBottom: '2px' };
const scaleDescStyle = { fontSize: '12.5px', color: 'var(--ink2)', lineHeight: 1.5 };
const pickedTag = { color: 'var(--accent)', fontSize: '11px', marginLeft: '6px', fontWeight: 500 };

const footStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '16px 24px',
  borderTop: '1px solid var(--line)',
  background: 'var(--paper)',
};
const btnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '40px',
  padding: '0 18px',
  borderRadius: '999px',
  fontSize: '13.5px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};
const ghostBtnStyle = { ...btnBase, background: 'transparent', border: '1px solid transparent', color: 'var(--ink2)' };
const accentBtnStyle = { ...btnBase, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' };
const accentBtnDisabledStyle = { ...btnBase, background: 'var(--line-dim)', color: 'var(--muted)', border: '1px solid var(--line)', cursor: 'not-allowed' };
